import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BranchProvider } from "@/context/BranchContext";
import { OpsRoleProvider } from "@/context/OpsRoleContext";
import AppLayout from "@/components/layout/AppLayout";
import SiteGate from "@/components/SiteGate";
import OpsLayout from "@/components/ops/OpsLayout";
import OpsToday from "@/pages/ops/OpsToday";
import OpsQueue from "@/pages/ops/OpsQueue";
import OpsTask from "@/pages/ops/OpsTask";
import OpsAttention from "@/pages/ops/OpsAttention";
import OpsBranches from "@/pages/ops/OpsBranches";
import OpsBranch from "@/pages/ops/OpsBranch";
import OpsTeam from "@/pages/ops/OpsTeam";
import OpsCapacity from "@/pages/ops/OpsCapacity";
import OpsManagement from "@/pages/ops/OpsManagement";
import SalesLayout from "@/components/sales/SalesLayout";
import SalesHome from "@/pages/sales/SalesHome";
import HealthCheckNew from "@/pages/sales/HealthCheckNew";
import HealthCheckAnalysis from "@/pages/sales/HealthCheckAnalysis";
import HealthCheckResult from "@/pages/sales/HealthCheckResult";
import HealthCheckReport from "@/pages/sales/HealthCheckReport";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Overview from "@/pages/Overview";
import GrowthScorePage from "@/pages/GrowthScorePage";
import GrowthForecast from "@/pages/GrowthForecast";
import Competitors from "@/pages/Competitors";
import Content from "@/pages/Content";
import SocialWeek from "@/pages/SocialWeek";
import Insights from "@/pages/Insights";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Help from "@/pages/Help";
import Account from "@/pages/Account";
import Integrations from "@/pages/Integrations";
import NicheComUsage from "@/pages/NicheComUsage";
import PropertyMarketing from "@/pages/PropertyMarketing";
import GroupDashboard from "@/pages/GroupDashboard";
import SharedPost from "@/pages/SharedPost";
import { StrategyProvider } from "@/context/StrategyContext";
import StrategyLayout from "@/components/strategy/StrategyLayout";
import StrategyHome from "@/pages/strategy/StrategyHome";
import Assessment from "@/pages/strategy/Assessment";
import AlignmentDashboard from "@/pages/strategy/AlignmentDashboard";
import Perspectives from "@/pages/strategy/Perspectives";
import Decisions from "@/pages/strategy/Decisions";
import Opportunities from "@/pages/strategy/Opportunities";
import RevenueModel from "@/pages/strategy/RevenueModel";
import Roadmap from "@/pages/strategy/Roadmap";
import Adviser from "@/pages/strategy/Adviser";
import Report from "@/pages/strategy/Report";
import Admin from "@/pages/strategy/Admin";

function Loader() {
  return (
    <div className="min-h-screen grid place-items-center" style={{ background: "#F7F8F6" }}>
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 rounded-full border-2 animate-spin" style={{ borderColor: "#E7E8E4", borderTopColor: "#93C020" }} />
        <span className="label-caps">Loading Growth</span>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function IndexHome() {
  const { isGroup } = useAuth();
  return isGroup ? <Navigate to="/app/group" replace /> : <Overview />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/share/post/:id" element={<SharedPost />} />
      <Route path="/login" element={<Login />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<IndexHome />} />
        <Route path="group" element={<GroupDashboard />} />
        <Route path="growth-score" element={<GrowthScorePage />} />
        <Route path="forecast" element={<GrowthForecast />} />
        <Route path="nichecom" element={<NicheComUsage />} />
        <Route path="property" element={<PropertyMarketing />} />
        <Route path="competitors" element={<Competitors />} />
        <Route path="content" element={<Content />} />
        <Route path="social-week" element={<SocialWeek />} />
        <Route path="insights" element={<Insights />} />
        <Route path="reports" element={<Reports />} />
        <Route path="account" element={<Account />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="settings" element={<Settings />} />
        <Route path="help" element={<Help />} />
      </Route>
      <Route path="/ops" element={<Protected><OpsLayout /></Protected>}>
        <Route index element={<OpsToday />} />
        <Route path="queue" element={<OpsQueue />} />
        <Route path="task/:id" element={<OpsTask />} />
        <Route path="attention" element={<OpsAttention />} />
        <Route path="branches" element={<OpsBranches />} />
        <Route path="branches/:id" element={<OpsBranch />} />
        <Route path="team" element={<OpsTeam />} />
        <Route path="capacity" element={<OpsCapacity />} />
        <Route path="management" element={<OpsManagement />} />
      </Route>
      <Route path="/sales" element={<Protected><SalesLayout /></Protected>}>
        <Route index element={<SalesHome />} />
        <Route path="new" element={<HealthCheckNew />} />
        <Route path="check/:id/analysis" element={<HealthCheckAnalysis />} />
        <Route path="check/:id" element={<HealthCheckResult />} />
        <Route path="check/:id/report" element={<HealthCheckReport />} />
      </Route>
      <Route path="/roadmap" element={<StrategyProvider><StrategyLayout /></StrategyProvider>}>
        <Route index element={<StrategyHome />} />
        <Route path="assess" element={<Assessment />} />
        <Route path="alignment" element={<AlignmentDashboard />} />
        <Route path="perspectives" element={<Perspectives />} />
        <Route path="decisions" element={<Decisions />} />
        <Route path="opportunities" element={<Opportunities />} />
        <Route path="revenue" element={<RevenueModel />} />
        <Route path="plan" element={<Roadmap />} />
        <Route path="adviser" element={<Adviser />} />
        <Route path="report" element={<Report />} />
        <Route path="admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <SiteGate>
      <AuthProvider>
        <BranchProvider>
          <OpsRoleProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="bottom-right" toastOptions={{ style: { fontFamily: "Manrope" } }} />
            </BrowserRouter>
          </OpsRoleProvider>
        </BranchProvider>
      </AuthProvider>
    </SiteGate>
  );
}
